<template>
  <el-drawer :visible.sync="show" :with-header="false" size="540px" :append-to-body="true" @opened="onOpened">
    <div v-if="form" class="pd">
      <!-- 顶部 -->
      <div class="pd__top">
        <el-image class="pd__img" :src="firstImg(form.url)" fit="contain">
          <div slot="error" class="pd__ph"><i class="el-icon-goods"></i></div>
          <div slot="placeholder" class="pd__ph"><i class="el-icon-goods"></i></div>
        </el-image>
        <div class="pd__head">
          <div class="pd__title">{{ form.name || $t("products.detailTitle") }}</div>
          <div v-if="form.no" class="pd__sku vtm-mono">{{ form.no }}</div>
          <span v-if="categoryName" class="vtm-pill vtm-pill--info">{{ categoryName }}</span>
        </div>
        <button class="pd__close" type="button" @click="show = false"><i class="el-icon-close"></i></button>
      </div>

      <!-- 可编辑主体（自动保存，无确认按钮） -->
      <div class="pd__scroll">
        <div class="pd__grid">
          <div class="pd__field">
            <label class="pd__label">{{ $t("addProduct.goodsName") }}</label>
            <el-input v-model="form.name" size="small" :placeholder="$t('addProduct.pProductName')"></el-input>
          </div>
          <div class="pd__field">
            <label class="pd__label">{{ $t("products.price") }}</label>
            <el-input v-model="form.costPrice" size="small" :placeholder="$t('addProduct.pPrice')"></el-input>
          </div>

          <div class="pd__field">
            <label class="pd__label">{{ $t("products.category") }}</label>
            <div class="pd__inline">
              <el-select v-model="form.typeId" size="small" filterable clearable :placeholder="$t('addProduct.pProductTypeId')">
                <el-option v-for="c in categories" :key="c.id" :label="c.name" :value="c.id"></el-option>
              </el-select>
              <el-button size="small" icon="el-icon-plus" @click="dialogCategory = true">{{ $t("products.newCategory") }}</el-button>
            </div>
          </div>
          <div class="pd__field">
            <label class="pd__label">{{ $t("products.sku") }}</label>
            <el-input v-model="form.no" size="small" :placeholder="$t('addProduct.pCode')"></el-input>
          </div>

          <div class="pd__field pd__field--full">
            <label class="pd__label">{{ $t("products.tags") }}</label>
            <div class="pd__inline">
              <el-select v-model="form.goodsTags" size="small" multiple collapse-tags filterable :placeholder="$t('common.select')">
                <el-option v-for="t in tags" :key="t.id" :label="t.name" :value="t.id"></el-option>
              </el-select>
              <el-button size="small" icon="el-icon-plus" @click="dialogTag = true">{{ $t("products.newTag") }}</el-button>
            </div>
          </div>

          <div class="pd__field pd__field--full">
            <label class="pd__label">{{ $t("products.image") }}</label>
            <media-upload v-model="form.url" :action="uploadUrl" accept=".jpg,.jpeg,.png,.gif" :image-max-size="1" />
          </div>

          <div class="pd__field pd__field--full">
            <label class="pd__label">{{ $t("products.description") }}</label>
            <el-input v-model="form.content" type="textarea" :rows="3" :placeholder="$t('addProduct.pDescribe')"></el-input>
          </div>

          <!-- 商品详情（富文本 introduction）只在完整编辑表单里能改 -->
          <div class="pd__field pd__field--full">
            <label class="pd__label">{{ $t("products.details") }}</label>
            <el-button size="small" icon="el-icon-edit" @click="editFull">{{ $t("products.editDetails") }}</el-button>
          </div>
        </div>

        <!-- 自动保存状态 -->
        <div class="pd__status" :class="'pd__status--' + saveState">
          <i v-if="saveState === 'saving'" class="el-icon-loading"></i>
          <i v-else-if="saveState === 'saved'" class="el-icon-circle-check"></i>
          <span>{{ statusText }}</span>
        </div>

        <!-- 危险操作 -->
        <div class="pd__danger" v-hasPerm="['product:delete']">
          <el-button type="danger" plain size="small" icon="el-icon-delete" @click="del">{{ $t("common.delete") }}</el-button>
        </div>
      </div>

      <!-- 内联新建分类 / 标签 -->
      <add-product-type :dialogProductType="dialogCategory" :productType="{}" v-on:cancel="onCategoryAdded" />
      <add-product-tag :dialogProductTag="dialogTag" :productTag="{}" v-on:cancel="onTagAdded" />
    </div>
  </el-drawer>
</template>

<script>
  // 商品详情飞出面板：字段就地编辑 + 自动保存（设计指南 §7.2 / §8.1）。
  // 打开即把 row 拷入本地 form；深度侦听 + 去抖(850ms) + 就绪标记防止初始化误存；
  // 保存走 goods/update，goodsTags 在提交时转回 [{goodsTagId}]。
  import config from "@/http/config";
  import addProductType from "_c/product/AddProductType";
  import addProductTag from "_c/product/AddProductTag";
  import mediaUpload from "_c/common/MediaUpload";

  export default {
    name: "ProductDetailFlyout",
    components: { addProductType, addProductTag, mediaUpload },
    props: {
      visible: { type: Boolean, default: false },
      product: { type: Object, default: null },
      categories: { type: Array, default: () => [] },
      tags: { type: Array, default: () => [] }
    },
    data() {
      return {
        form: null,
        ready: false,
        baseline: "",
        saveState: "idle",
        saveTimer: null,
        revertTimer: null,
        dialogCategory: false,
        dialogTag: false,
        uploadUrl: config.baseUrl + this.$url.Upload.product
      };
    },
    computed: {
      show: {
        get() {
          return this.visible;
        },
        set(v) {
          this.$emit("update:visible", v);
        }
      },
      categoryName() {
        if (!this.form || !this.form.typeId) {
          return this.form && this.form.typeName ? this.form.typeName : "";
        }
        const c = this.categories.find(x => x.id === this.form.typeId);
        return c ? c.name : this.form.typeName || "";
      },
      statusText() {
        if (this.saveState === "saving") {
          return this.$t("products.saving");
        }
        if (this.saveState === "saved") {
          return this.$t("products.saved");
        }
        return this.$t("products.autosaveHint");
      }
    },
    watch: {
      form: {
        deep: true,
        handler() {
          if (!this.ready || !this.form) {
            return;
          }
          if (JSON.stringify(this.form) === this.baseline) {
            return;
          }
          this.scheduleSave();
        }
      }
    },
    methods: {
      // 打开完整编辑表单（含商品详情富文本），关闭当前面板
      editFull() {
        // 面板里还没自动保存的改动先存掉，并带进完整表单（标签转回 [{goodsTagId}]）
        if (this.saveTimer) {
          clearTimeout(this.saveTimer);
          this.saveTimer = null;
          this.doSave();
        }
        const row = Object.assign({}, this.product, this.form, {
          goodsTags: (this.form.goodsTags || []).map(id => ({ goodsTagId: id }))
        });
        this.$emit("edit-full", row);
        this.show = false;
      },
      firstImg(url) {
        if (!url) {
          return "";
        }
        return String(url).split(",")[0].trim();
      },
      onOpened() {
        this.initForm();
      },
      initForm() {
        if (!this.product) {
          return;
        }
        this.ready = false;
        const src = this.product;
        // goodsTags: [{goodsTagId}] → [id]
        let tagIds = [];
        if (Array.isArray(src.goodsTags)) {
          tagIds = src.goodsTags.map(t => (t && t.goodsTagId !== undefined ? t.goodsTagId : t)).filter(v => v !== undefined && v !== null);
        }
        this.form = {
          id: src.id,
          name: src.name || "",
          costPrice: src.costPrice !== undefined && src.costPrice !== null ? src.costPrice : "",
          typeId: src.typeId || "",
          typeName: src.typeName || "",
          no: src.no || "",
          url: src.url || "",
          content: src.content || "",
          introduction: src.introduction || "",
          notes: src.notes || "",
          goodsTags: tagIds
        };
        this.baseline = JSON.stringify(this.form);
        this.saveState = "idle";
        this.$nextTick(() => {
          this.ready = true;
        });
      },
      scheduleSave() {
        if (this.saveTimer) {
          clearTimeout(this.saveTimer);
        }
        // 触发后清空 saveTimer，editFull 才能据此判断“还有没存的改动”
        this.saveTimer = setTimeout(() => {
          this.saveTimer = null;
          this.doSave();
        }, 850);
      },
      doSave() {
        if (!this.form || !this.form.name || this.form.costPrice === "" || this.form.costPrice === null) {
          // 名称/价格必填，缺失则不保存（保持 idle）
          return;
        }
        this.saveState = "saving";
        const payload = Object.assign({}, this.form, {
          goodsTags: (this.form.goodsTags || []).map(id => ({ goodsTagId: id }))
        });
        this.$api
          .request(this.$url.Product.update, this.$method.put, payload)
          .then(res => {
            if (res.code === 0) {
              this.baseline = JSON.stringify(this.form);
              this.saveState = "saved";
              this.$emit("saved");
              if (this.revertTimer) {
                clearTimeout(this.revertTimer);
              }
              this.revertTimer = setTimeout(() => {
                this.saveState = "idle";
              }, 2000);
            } else {
              this.saveState = "idle";
              this.$message.warning(res.msg || this.$t("addProduct.error"));
            }
          })
          .catch(err => {
            this.saveState = "idle";
            this.$message.warning((err && err.msg) || this.$t("addProduct.error"));
          });
      },
      onCategoryAdded(dialog, status) {
        this.dialogCategory = dialog;
        if (status) {
          this.$emit("taxonomy-changed");
        }
      },
      onTagAdded(dialog, status) {
        this.dialogTag = dialog;
        if (status) {
          this.$emit("taxonomy-changed");
        }
      },
      del() {
        this.$confirm(this.$t("products.deleteConfirm"), this.$t("products.deleteProduct"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          this.$api.request(this.$url.Product.delete, this.$method.delete, { id: this.form.id }).then(res => {
            if (res.code === 0) {
              this.$notify({ title: this.$t("common.tip"), message: res.msg, type: "success" });
              this.$emit("deleted", this.form.id);
              this.show = false;
            } else {
              this.$message.error(res.msg);
            }
          });
        });
      }
    }
  };
</script>

<style scoped lang="scss">
  .pd {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .pd__top {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 20px 20px 16px;
    border-bottom: 1px solid var(--vtm-line);
  }
  .pd__img {
    width: 64px;
    height: 64px;
    flex-shrink: 0;
    border-radius: var(--vtm-r-md);
    background: var(--vtm-well);
    overflow: hidden;
  }
  .pd__ph {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: var(--vtm-ink-3);
    font-size: 22px;
    background: var(--vtm-well);
  }
  .pd__head {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .pd__title {
    font-family: var(--vtm-display);
    font-size: 18px;
    font-weight: 600;
    color: var(--vtm-heading);
    line-height: 1.3;
    word-break: break-word;
  }
  .pd__sku {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }
  .pd__close {
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: var(--vtm-ink-3);
    font-size: 18px;
    cursor: pointer;
    padding: 2px;

    &:hover {
      color: var(--vtm-crit);
    }
  }

  .pd__scroll {
    flex: 1;
    overflow-y: auto;
    padding: 18px 20px 24px;
  }

  .pd__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  .pd__field {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
  }
  .pd__field--full {
    grid-column: 1 / -1;
  }
  .pd__label {
    font-size: 12px;
    font-weight: 600;
    color: var(--vtm-ink-2);
  }
  .pd__inline {
    display: flex;
    gap: 8px;

    .el-select {
      flex: 1;
    }
  }

  .pd__status {
    display: flex;
    align-items: center;
    gap: 6px;
    justify-content: flex-end;
    margin-top: 16px;
    font-size: 12px;
    color: var(--vtm-ink-3);
    min-height: 18px;
  }
  .pd__status--saved {
    color: var(--vtm-ok);
  }

  .pd__danger {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid var(--vtm-line);
  }
</style>
